<template>
  <div>
    <div v-if="isLoaded">
      <div>
        <TheDetailHeader
          :title="$t(title)"
          :parent="{ label: $t('pages.missedCalls.missedCallsSettingsPage.title'), to: { name: 'MissedCallsList' } }"
        />
        <div v-if="filtered || !noCustomer" class="mb-6 flex items-center px-6 sm:px-12">
          <ThePageSettings
            ref="ThePageSettings"
            :display-store-selector="true"
            :display-date-range-picker="false"
            :display-filters="true"
            @store-selected="fetchData"
            @update:search-field="search"
            @validate-filters="validateFilters"
          >
            <template #filtersDescription>
              {{ $t('pages.missedCalls.missedCallsAnalyticsPage.filters.description') }}
            </template>
            <template #filtersContent>
              <div class="space-y-8">
                <section class="space-y-4">
                  <h3 class="text-sm font-semibold text-foreground">
                    {{ $t('pages.missedCalls.missedCallsAnalyticsPage.filters.engagement') }}
                  </h3>
                  <fieldset class="space-y-2">
                    <legend class="text-xs font-medium text-muted-foreground">
                      {{ $t('pages.missedCalls.missedCallsAnalyticsPage.filters.converted') }}
                    </legend>
                    <div class="grid grid-cols-3 gap-3">
                      <AppCheckboxField
                        id="isClicked"
                        :model-value="filters.isClicked === 1"
                        :label="$t('pages.missedCalls.missedCallsAnalyticsPage.filters.yes')"
                        @update:model-value="(checked) => (filters.isClicked = checked ? 1 : '')"
                      />
                      <AppCheckboxField
                        id="isNotClicked"
                        :model-value="filters.isClicked === 0"
                        :label="$t('pages.missedCalls.missedCallsAnalyticsPage.filters.no')"
                        @update:model-value="(checked) => (filters.isClicked = checked ? 0 : '')"
                      />
                    </div>
                  </fieldset>
                  <fieldset class="space-y-2">
                    <legend class="text-xs font-medium text-muted-foreground">
                      {{ $t('pages.missedCalls.missedCallsAnalyticsPage.filters.calledAt') }}
                    </legend>
                    <LitepieDatepicker
                      ref="litepieCalledAt"
                      v-model="filters.calledAt"
                      :formatter="{
                        date: 'DD/MM/YYYY',
                        month: 'MMM'
                      }"
                      as-single
                      use-range
                      i18n="fr"
                      @click="litepieCalledAt.isShow = !litepieCalledAt.isShow"
                    />
                  </fieldset>
                </section>
              </div>
            </template>
          </ThePageSettings>
        </div>
        <div class="mt-4 px-6 pb-6 sm:px-12 sm:pb-12">
          <div v-if="missedCallsStatistics?.missedCallCount">
            <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
              <AppCard
                title="pages.missedCalls.missedCallsAnalyticsPage.results.missedCallsCount"
                :content="formatNumber(missedCallsStatistics.missedCallCount)"
              />
              <AppCard
                title="pages.missedCalls.missedCallsAnalyticsPage.results.customersConverted"
                :content="formatNumber(missedCallsStatistics.missedCallClicked)"
              />
              <AppCard
                title="pages.missedCalls.missedCallsAnalyticsPage.results.conversionRate"
                :content="missedCallsStatistics.missedCallClicked ? missedCallsConvertedRate : '0%'"
              />
            </dl>

            <div class="mt-8 flex flex-col">
              <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <AppTable
                    :columns="translatedColumns"
                    :rows="table.rows"
                    :pagination="table.pagination"
                    :sort="table.sort"
                    @update:change-page="changePage"
                    @update:change-sort="changeSort"
                  >
                    <template #td-customer="{ data }">
                      <RowCustomer
                        :id="data.customer.id"
                        :country-flag="data.customer.countryFlag ?? null"
                        :has-optin="data.customer.hasOptin"
                        :name="data.customer.name"
                        :phone="data.customer.phone"
                      />
                    </template>
                    <template #td-calledAt="{ data }">
                      <div>
                        <div class="text-sm font-medium text-foreground">{{ data.calledAt.date }}</div>
                        <div v-if="data.calledAt.time" class="text-sm text-muted-foreground">
                          {{ data.calledAt.time }}
                        </div>
                      </div>
                    </template>
                    <template #td-isClicked="{ data }">
                      <CircleCheck v-if="data.isClicked.isClicked" class="h-5 w-5 text-green-500" />
                      <CircleX v-else class="h-5 w-5 text-red-500" />
                    </template>
                  </AppTable>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="filtered">
            <TheEmptyState
              module="missedCalls"
              :title="$t(`pages.missedCalls.emptyState.filtered`)"
              :primary-button="emptyStateNoResultButton"
              @on-click="resetFilters"
            />
          </div>
          <div v-else-if="noCustomer">
            <TheEmptyState module="missedCalls" :title="$t(`pages.missedCalls.emptyState.noCustomer`)" />
          </div>
          <TheEmptyState
            v-else
            module="missedCalls"
            link="MissedCallsList"
            :title="$t(`pages.missedCalls.emptyState.title`)"
            :description="$t(`pages.missedCalls.emptyState.description`)"
          />
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import LitepieDatepicker from 'litepie-datepicker-tw3'
import { CircleCheck, CircleX } from 'lucide-vue-next'

import AppCard from '@/components/ui/AppCard.vue'
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import TheEmptyState from '@/components/layout/TheEmptyState.vue'
import ThePageSettings from '@/components/layout/ThePageSettings.vue'
import AppCheckboxField from '@/components/ui/AppCheckboxField.vue'
import useLocalStorage from '@/composables/useLocalStorage'
import useStore from '@/composables/store'
import useNumber from '@/composables/number'

const defaultFilter = {
  calledAt: {
    from: dayjs().subtract(6, 'month').format('DD/MM/YYYY'),
    to: dayjs().format('DD/MM/YYYY')
  },
  isClicked: ''
}

export default {
  components: {
    CircleCheck,
    CircleX,
    TheLoadingPage,
    AppCard,
    TheEmptyState,
    ThePageSettings,
    AppCheckboxField,
    LitepieDatepicker
  },
  setup() {
    const litepieCalledAt = ref(null)
    const notification = useNotification()
    const { permissions, stores: storesItem } = useLocalStorage()
    const { selectedStore } = useStore()
    const { formatNumber } = useNumber()
    const { t } = useI18n()

    const emptyStateNoResultButton = {
      text: t(`pages.customer.customerPage.noResults.reset`),
      icon: 'HeroAdjustmentsVerticalIconSolid',
      isOutlined: false
    }

    return {
      litepieCalledAt,
      notification,
      permissions,
      storesItem,
      selectedStore,
      formatNumber,
      emptyStateNoResultButton
    }
  },
  data: () => {
    return {
      isLoaded: false,
      stores: [],
      table: {
        sort: {
          sortBy: 'calledAt',
          sortOrder: 'DESC'
        },
        pagination: {
          currentPage: 1,
          pageSize: 10
        },
        columns: {
          customer: {
            id: 'phone',
            label: 'pages.missedCalls.missedCallsAnalyticsPage.table.customer'
          },
          store: {
            id: 'store',
            label: 'pages.missedCalls.missedCallsAnalyticsPage.table.store'
          },
          calledAt: {
            id: 'calledAt',
            label: 'pages.missedCalls.missedCallsAnalyticsPage.table.date',
            sortable: true
          },
          isClicked: {
            id: 'isClicked',
            label: 'pages.missedCalls.missedCallsAnalyticsPage.table.converted',
            sortable: true
          }
        },
        rows: []
      },
      missedCallsStatistics: null,
      pageTitle: {
        title: 'pages.missedCalls.missedCallsAnalyticsPage.title',
        description: 'pages.missedCalls.missedCallsAnalyticsPage.description'
      },
      searchText: null,
      currentFilters: defaultFilter,
      defaultFilter: defaultFilter,
      filters: JSON.parse(JSON.stringify(defaultFilter)), // Create a non reactive copy
      now: dayjs().format('DD/MM/YYYY'),
      sixMonthsAgo: dayjs().subtract(6, 'month').format('DD/MM/YYYY')
    }
  },
  computed: {
    translatedColumns() {
      return Object.fromEntries(
        Object.entries(this.table.columns).map(([key, col]) => [key, { ...col, label: this.$t(col.label) }])
      )
    },
    title() {
      return 'pages.missedCalls.missedCallsAnalyticsPage.title'
    },
    description() {
      return 'pages.missedCalls.missedCallsAnalyticsPage.description'
    },
    crumbs() {
      return [
        {
          name: this.$t('pages.missedCalls.missedCallsSettingsPage.title'),
          route: 'MissedCallsList'
        },
        {
          name: this.$t('pages.missedCalls.missedCallsAnalyticsPage.crumbs')
        }
      ]
    },
    filtered() {
      return JSON.stringify(this.currentFilters) != JSON.stringify(this.defaultFilter)
    },
    noCustomer() {
      return this.missedCallsStatistics?.missedCallCount > 0 && this.table.rows.length === 0
    },
    missedCallsConvertedRate() {
      const percent = (this.missedCallsStatistics.missedCallClicked / this.missedCallsStatistics.missedCallCount) * 100
      return `${percent.toFixed(2)}%`
    }
  },
  created() {
    this.storesItem.forEach((store) => this.stores.push(store.id))
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.fetchMissedCallsStatistics()
      this.fetchMissedCalls()
    },
    async resetFilters() {
      this.isLoaded = false
      this.searchText = null
      this.filters = { ...defaultFilter }
      await this.validateFilters()
    },
    fetchMissedCallsStatistics() {
      this.axios
        .get('/missed-calls/statistics', {
          params: {
            ...this.getFiltersParams()
          }
        })
        .then((response) => {
          this.missedCallsStatistics = response.data.data
        })
        .catch(() => {
          this.notification.create('error', {
            text: this.$t('pages.missedCalls.missedCallsAnalyticsPage.error'),
            timeout: 2000
          })
        })
    },
    changeSort(sort) {
      this.table.sort = sort
      this.fetchMissedCalls()
    },
    changePage(page) {
      this.table.pagination.currentPage = page
      this.fetchMissedCalls()
    },
    search(event) {
      this.searchText = event
      this.table.pagination.currentPage = 1
      this.fetchData()
    },
    validateFilters() {
      this.table.pagination.currentPage = 1
      this.currentFilters = JSON.parse(JSON.stringify(this.filters))
      this.fetchData()
    },
    convertDateFormat(date) {
      if (date === '') {
        return null
      }
      return dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
    },
    getFiltersParams() {
      return {
        sortBy: this.table.sort.sortBy,
        sortOrder: this.table.sort.sortOrder,
        storeId: this.selectedStore > 0 ? this.selectedStore : null,
        callDateFrom: this.convertDateFormat(this.filters.calledAt.from) ?? this.convertDateFormat(this.sixMonthsAgo),
        callDateTo: this.convertDateFormat(this.filters.calledAt.to) ?? this.convertDateFormat(this.now),
        isClicked: this.filters.isClicked
      }
    },
    fetchMissedCalls() {
      this.axios
        .get('/missed-calls', {
          params: {
            ...this.getFiltersParams(),
            pageSize: this.table.pagination.pageSize,
            page: this.table.pagination.currentPage,
            sortBy: this.table.sort.sortBy,
            sortOrder: this.table.sort.sortOrder
          }
        })
        .then((response) => {
          const rows = []
          response.data.data.forEach((row) => {
            rows.push({
              id: row.id,
              data: {
                customer: {
                  id: row.customerMerchant?.id,
                  phone: row.phone,
                  name: row.customerMerchant?.firstName,
                  gender: row.customerMerchant?.gender,
                  hasOptin: row.customerMerchant?.hasOptin,
                  countryFlag: row.customerMerchant?.country?.flag
                },
                store: row.store.name,
                calledAt: {
                  date: row.calledAt !== null ? new Date(row.calledAt).toLocaleDateString('fr-FR') : '-',
                  time: row.calledAt !== null ? new Date(row.calledAt).toLocaleTimeString('fr-FR') : ''
                },
                isClicked: { isClicked: row.isClicked }
              }
            })
          })
          this.table.rows = rows
          this.table.pagination = {
            currentPage: response.data.meta.current_page,
            lastPage: response.data.meta.last_page,
            total: response.data.meta.total
          }
        })
        .catch(() => {
          this.notification.create('error', {
            text: this.$t('pages.missedCalls.missedCallsAnalyticsPage.error'),
            timeout: 2000
          })
        })
        .finally(() => (this.isLoaded = true))
    }
  }
}
</script>
